// Mirrors the server's pre-tokenization step (`stripMarkup` in `word-cloud-phrase-matcher.ts`) and
// has to stay identical to it. Notes carry HTML from imports, integrations and pasted rich text,
// and the tokenizer maps every non-letter to a space — so `&nbsp;` and `<strong>` become the words
// "nbsp" and "strong", sitting between a phrase's words. The server counts a note with its markup
// removed, so a client testing `matchedForms` against the raw note finds nothing and highlights
// nothing for a note the count already includes. The stripped string is also what the notes modal
// renders, so anything left behind is shown to the reader.
//
// A block tag becomes a newline rather than a space, because it ends a clause: fusing two
// paragraphs would let a phrase span them, and `matchedFormsPattern` will not cross a newline.
// A URL goes the same way, whole and slug included: its hyphens would otherwise read as a compound,
// and a phrase must not match across the gap it leaves. It runs last of the markup rules, which is
// load-bearing: `&lt;`/`&gt;` are not in its class, so ahead of ESCAPED_TAG it ate an escaped
// `href="…"&gt;outcome&lt;/a&gt;` whole and left `href` behind as a term.
// Character references become spaces rather than their characters, as the server does, or the two
// sides disagree on where a word ends. Whitespace is otherwise left alone.

// Dropped with their content: stripping the tags alone leaves the CSS or JS behind as prose, and
// `font-family` then reads as a hyphenated compound. An unclosed one falls through to the tag
// rules, because a greedy match would swallow the rest of the note.
const SCRIPT_OR_STYLE = /<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi;
const URL = /\b(?:https?:\/\/|www\.)[^\s<>]+/gi;
const BLOCK_TAG =
  /<\/?(?:p|div|br|hr|li|[uo]l|tr|t[dh]|table|thead|tbody|h[1-6]|blockquote|pre|section|article|header|footer|figure|figcaption|d[ltd]|address|main|nav|aside|form|fieldset)\b[^>]*>/gi;
// A letter or "/" must follow the "<", so a plain-text "coolant < threshold >" survives.
const HTML_TAG = /<\/?[a-zA-Z][^>]*>/g;
// Escaped markup, removed before the references are: decoding `&lt;strong&gt;` to spaces leaves the
// tag name behind as a word. A known name is not enough, since "a", "b", "i", "p" and "u" are words
// too — the shape of a tag is required as well, one bare identifier included (`&lt;td nowrap&gt;`).
const ESCAPED_TAG =
  /&lt;\/?(?:p|div|br|hr|span|strong|em|b|i|u|a|li|[uo]l|tr|t[dh]|table|thead|tbody|h[1-6]|blockquote|pre|font)(?:&gt;|\s*\/&gt;|\s[^&]*?=[^&]*?&gt;|\s[a-zA-Z-]+\s*\/?&gt;)/gi;
const CHARACTER_REFERENCE = /&(?:#\d+|#[xX][0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]*);/g;

const stripMarkup = (text: string): string =>
  text
    .replace(SCRIPT_OR_STYLE, "\n")
    .replace(BLOCK_TAG, "\n")
    .replace(HTML_TAG, " ")
    .replace(ESCAPED_TAG, " ")
    .replace(URL, "\n")
    .replace(CHARACTER_REFERENCE, " ");

export default stripMarkup;
